import { IGenericBaryFormGroup } from './GenericBaryFormGroup';
import { FormGroupHelper } from './FormGroupHelper';
import { TransportControls } from './builder/TransportFormBuilder';
import { FormArray, FormGroup, ValidatorFn } from '@angular/forms';
import { Transport } from '../transport.model';
import { RegisteredAddress } from '../registered-address.model';
import { RegisteredAddressMapper } from '../../app/shared/mapper/registered-address-mapper';
import { TransportItem } from '../transport-item.model';
import { BaryFormFactoryService } from '../../services/bary-form-factory.service';
import { TransportItemFormBuilder } from './builder/TransportItemFormBuilder';
import { patchObject } from '../../app/shared/util/object-util';
import { TransportMapper } from '../../app/shared/mapper/transport-mapper';
import { ITransportExtraCondition } from '../transport-extra-condition.model';
import { TransportExtraConditionControls } from '../transport-extra-condition.controls';
import { ServiceTypeEnum } from '../enums/offer-service-type.enum';

export class TransportFormGroup extends FormGroupHelper implements IGenericBaryFormGroup<TransportControls> {
  protected declare form: FormGroup;
  name = 'transportForm';
  updated = false;
  controls: TransportControls;

  constructor(formName: string, controls: TransportControls, validators?: ValidatorFn[]) {
    super();
    this.name = formName;
    this.form = this.newFormGroup(controls, validators);
    this.controls = controls;
  }

  public getAngularForm(): FormGroup {
    return this.form;
  }

  public updateForm(model: Transport, options: { emitEvent: boolean } = { emitEvent: true }): void {
    this.form.patchValue(model, options);
    this.updated = true;
  }

  public updateFormFromRegisteredAddress(registeredAddress: RegisteredAddress, isPickup: boolean): void {
    const address = RegisteredAddressMapper.getAddress(registeredAddress);
    const contact = RegisteredAddressMapper.getContact(registeredAddress);
    if (isPickup) {
      this.controls.pickupAddress.updateForm(address, true);
      this.controls.pickupContact.updateForm(contact);
      if (this.controls.pickupLoadingConstraint) {
        this.controls.pickupLoadingConstraint.setValue(registeredAddress.loadingConstraint);
      }
      if (this.controls.pickupAccessConstraint) {
        this.controls.pickupAccessConstraint.setValue(registeredAddress.accessConstraint);
      }

      this.controls.pickupRegisteredAddressId.setValue(registeredAddress.id);
    } else {
      this.controls.deliveryAddress.updateForm(address, true);
      this.controls.deliveryContact.updateForm(contact);
      if (this.controls.deliveryLoadingConstraint) {
        this.controls.deliveryLoadingConstraint.setValue(registeredAddress.loadingConstraint);
      }
      if (this.controls.deliveryAccessConstraint) {
        this.controls.deliveryAccessConstraint.setValue(registeredAddress.accessConstraint);
      }

      this.controls.deliveryRegisteredAddressId.setValue(registeredAddress.id);
    }
  }

  public updateAccessConstraint(accessConstraint: string, isPickup: boolean): void {
    if (isPickup) {
      this.controls.pickupAccessConstraint.setValue(accessConstraint);
    } else {
      this.controls.deliveryAccessConstraint.setValue(accessConstraint);
    }
  }

  public updateLoadingConstraint(loadingConstraint: string, isPickup: boolean): void {
    if (isPickup) {
      this.controls.pickupLoadingConstraint.setValue(loadingConstraint);
    } else {
      this.controls.deliveryLoadingConstraint.setValue(loadingConstraint);
    }
  }

  public addTransportItem(transportItem: TransportItem, bcf: BaryFormFactoryService): void {
    const tif = new TransportItemFormBuilder(bcf).init(transportItem.serialize()).build();
    this.controls.items.push(tif);
    const items = this.form.controls['items'] as FormArray;
    items.push(tif.getAngularForm());
  }

  public removeTransportItem(index: number): void {
    this.controls.items.splice(index, 1);
    const items = this.form.controls['items'] as FormArray;
    items.removeAt(index);
  }

  public  updateModel(model: Transport): void {
    patchObject(model, this.form.value);
    TransportMapper.updatePickupAddress(model, this.controls.pickupAddress.value(), this.controls.pickupContact.value());
    TransportMapper.updateDeliveryAddress(model, this.controls.deliveryAddress.value(), this.controls.deliveryContact.value());
  }

  public addTransportExtraCondition(bcf: BaryFormFactoryService, isAdr: boolean, transportExtraCondition?: ITransportExtraCondition): void {
    const txc = bcf.makeTransportExtraConditionsForm(isAdr, transportExtraCondition);
    this.controls.transportExtraConditions.push(txc);
    const extraConditions = this.form.controls['transportExtraConditions'] as FormArray;
    extraConditions.push(txc.getAngularForm());
  }

  public addTransportExtraConditionForm(transportExtraConditionForm: IGenericBaryFormGroup<TransportExtraConditionControls>): void {
    this.controls.transportExtraConditions.push(transportExtraConditionForm);
    const extraConditions = this.form.controls['transportExtraConditions'] as FormArray;
    extraConditions.push(transportExtraConditionForm.getAngularForm());
  }

  public removeTransportExtraCondition(index: number): void {
    this.controls.transportExtraConditions.splice(index, 1);
    const extraConditions = this.form.controls['transportExtraConditions'] as FormArray;
    extraConditions.removeAt(index);
  }

  public resetTransportExtraCondition(): void {
    this.controls.transportExtraConditions.splice(0, this.controls.transportExtraConditions.length);
    (this.form.controls['transportExtraConditions'] as FormArray).clear();
  }

  public value(): any {
    return this.form.value;
  }

  public showAddItem(): boolean {
    return (this.controls.serviceType.value === ServiceTypeEnum.FTL
      && this.controls.items.length === 0) || this.controls.serviceType.value === ServiceTypeEnum.LTL
  }
}
